// Storage "Download to": destination picker + the download dock, on src/ui.
//
// The modal reads THE MACHINE THE FILE IS GOING TO before a transfer starts: what the browser can do, how much room
// it reports, which folders this user has downloaded to before and whether those grants are still live, and what is
// already in the folder they pick (including a file of the same name that would be replaced). Everything shown is
// measured at open time. Where the web platform cannot tell us something (real free space, the browser's configured
// download path) the UI says so instead of guessing. The transfer logic lives in lib/storageDownload.js (unchanged
// apart from its two "unknown" placeholders).

import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, CircleStop, FolderPlus, RotateCw, X } from 'lucide-react';
import { formatBytes } from '../lib/folderUpload';
import {
  probeMachine, loadDestinations, rememberDestination, forgetDestination,
  pickDestination, ensureWritePermission, inspectDestination, uniqueNameIn,
  startFolderDownload, startBrowserDownload, subscribeDownloads, dismissDownload,
  resumeDownload, fmtRate, fmtEta,
} from '../lib/storageDownload';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Radio from '../ui/Radio';
import Skeleton from '../ui/Skeleton';
import { clean } from '../shell/copy';
import { useUploads } from '../shell/uploads';

const BROWSER_DEST = '__browser__';

const PERMISSION = {
  granted:     { tone: 'ok', label: 'Access granted' },
  denied:      { tone: 'warn', label: 'Access denied: allow it again to use it' },
  unavailable: { tone: 'danger', label: 'No longer reachable' },
  prompt:      { tone: 'warn', label: 'Needs your permission again' },
};

// ── Modal ─────────────────────────────────────────────────────────────────────

export default function StorageDownloadModal({ file, api, base, onClose }) {
  const [machine, setMachine]       = useState(null);
  const [remembered, setRemembered] = useState([]);
  const [choice, setChoice]         = useState(null);      // dest key | BROWSER_DEST | null
  const [handle, setHandle]         = useState(null);      // the chosen directory handle
  const [inspection, setInspection] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [name, setName]             = useState(file.fileName);
  const [collisionMode, setCollisionMode] = useState('keep'); // 'keep' | 'overwrite'
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState(null);
  const [showMachine, setShowMachine] = useState(false);

  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // Probe the destination machine and restore prior folders on open. Both best-effort.
  useEffect(() => {
    (async () => {
      const m = await probeMachine();
      if (!alive.current) return;
      setMachine(m);
      const dests = await loadDestinations();
      if (!alive.current) return;
      setRemembered(dests);
      // Preselect the most recent still-granted folder: the common case is several files to the same place in a row.
      const ready = dests.find(d => d.permission === 'granted') || dests[0] || null;
      if (m.canPickFolder && ready) { setChoice(ready.key); setHandle(ready.handle); }
      else setChoice(BROWSER_DEST);
    })();
  }, []);

  const refreshInspection = useCallback(async (h, fileName) => {
    if (!h) { setInspection(null); return; }
    setInspecting(true);
    try {
      const state = await inspectDestination(h, fileName);
      if (alive.current) setInspection(state);
    } finally { if (alive.current) setInspecting(false); }
  }, []);

  // Debounced: a folder scan is real I/O on the user's disk.
  useEffect(() => {
    if (choice === BROWSER_DEST || !handle) { setInspection(null); return undefined; }
    const t = setTimeout(() => refreshInspection(handle, name), 300);
    return () => clearTimeout(t);
  }, [handle, name, choice, refreshInspection]);

  async function choosePicker() {
    setError(null);
    try {
      const picked = await pickDestination();
      if (!picked) return;   // the user dismissed the picker
      // A folder already in the list selects that row instead of adding a second one; isSameEntry is the identity.
      let existing = null;
      for (const d of remembered) {
        // eslint-disable-next-line no-await-in-loop
        try { if (await d.handle.isSameEntry(picked)) { existing = d; break; } } catch { /* dead handle */ }
      }
      if (existing) {
        setRemembered(prev => prev.map(d => (d.key === existing.key ? { ...d, permission: 'granted', handle: picked } : d)));
        setChoice(existing.key);
        setHandle(picked);
        return;
      }
      setHandle(picked);
      setChoice('__picked__');
      setRemembered(prev => [
        { key: '__picked__', name: picked.name, handle: picked, permission: 'granted', lastUsedAt: Date.now() },
        ...prev.filter(d => d.key !== '__picked__'),
      ]);
    } catch (err) {
      setError(err?.message || 'The folder picker did not open.');
    }
  }

  function selectRemembered(dest) { setError(null); setChoice(dest.key); setHandle(dest.handle); }

  async function grant(dest) {
    setError(null);
    const state = await ensureWritePermission(dest.handle);
    setRemembered(prev => prev.map(d => (d.key === dest.key ? { ...d, permission: state } : d)));
    if (state === 'granted') refreshInspection(dest.handle, name);
    else setError('Permission for that folder was not granted. Choose it again to allow it.');
  }

  async function drop(dest) {
    await forgetDestination(dest.key);
    setRemembered(prev => prev.filter(d => d.key !== dest.key));
    if (choice === dest.key) { setChoice(BROWSER_DEST); setHandle(null); }
  }

  const collision  = inspection?.collision || null;
  const nameIsDir  = collision?.isDirectory === true;
  const sizeBytes  = Number(file.fileSizeBytes) || 0;
  const tightSpace = machine?.headroomBytes != null && sizeBytes > 0 && sizeBytes > machine.headroomBytes;

  async function start() {
    setBusy(true); setError(null);
    try {
      if (choice === BROWSER_DEST || !handle) {
        await startBrowserDownload({ api, base, file });
        onClose();
        return;
      }
      // This click is the user gesture the permission prompt needs; any later in the flow the browser rejects it.
      const perm = await ensureWritePermission(handle);
      if (perm !== 'granted') {
        setError('That folder is not writable by this page. Allow access, or pick another folder.');
        setBusy(false);
        return;
      }
      const trimmed = (name || '').trim() || file.fileName;
      if (/[\\/]/.test(trimmed)) {
        setError('A file name cannot contain a slash.');
        setBusy(false);
        return;
      }
      // Re-check the collision at the last moment: another download can have landed the same name.
      const fresh = await inspectDestination(handle, trimmed);
      if (fresh.collision?.isDirectory) {
        setError('A folder in that destination already uses this name. Rename the download.');
        setBusy(false);
        return;
      }
      const overwrite  = collisionMode === 'overwrite';
      const targetName = (!overwrite && fresh.collision) ? await uniqueNameIn(handle, trimmed) : trimmed;
      await rememberDestination(handle);
      startFolderDownload({ api, base, file, dirHandle: handle, targetName, overwrite });
      onClose();
    } catch (err) {
      setError(err?.message || 'The download did not start. Try again.');
      setBusy(false);
    }
  }

  const usingFolder = choice !== BROWSER_DEST && !!handle;

  return (
    <Modal open onClose={() => !busy && onClose()} title="Download to"
      footer={(
        <>
          <Button variant="quiet" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!machine || nameIsDir || (usingFolder && !(name || '').trim())} onClick={start} data-testid="storage-download-start">
            {usingFolder ? `Download to ${handle.name}` : 'Download'}
          </Button>
        </>
      )}>
      <div className="ui-stack">
        <p className="ui-p ui-dense ui-text-2">{clean(file.fileName)}, <span className="ui-tc">{formatBytes(file.fileSizeBytes)}</span></p>
        {error && <p className="ui-p ui-field__error" role="alert">{error}</p>}

        {!machine ? (
          <div className="ui-stack ui-stack--tight" role="status" aria-label="Reading this machine">
            <Skeleton width="60%" /><Skeleton width="80%" /><Skeleton width="40%" />
          </div>
        ) : (
          <>
            <fieldset className="ui-stack ui-stack--tight ui-fieldset">
              <legend className="ui-field__label">Destination</legend>
              {machine.canPickFolder ? (
                <>
                  {remembered.map(dest => {
                    const p = PERMISSION[dest.permission] || PERMISSION.prompt;
                    return (
                      <div key={dest.key} className="ui-cluster">
                        <Radio name="storage-dest" checked={choice === dest.key} onChange={() => selectRemembered(dest)} label={dest.name} />
                        <Badge tone={p.tone}>{p.label}</Badge>
                        <span className="ui-spacer" />
                        {dest.permission === 'granted'
                          ? <Button variant="quiet" onClick={() => drop(dest)}>Forget</Button>
                          : <Button variant="quiet" onClick={() => grant(dest)}>Allow</Button>}
                      </div>
                    );
                  })}
                  <span><Button variant="secondary" icon={FolderPlus} onClick={choosePicker}>Choose a folder on this machine</Button></span>
                  <div className="ui-stack ui-stack--tight">
                    <Radio name="storage-dest" checked={choice === BROWSER_DEST} onChange={() => { setChoice(BROWSER_DEST); setHandle(null); }}
                      label="Browser downloads (default)" />
                    <span className="ui-meta">Goes wherever {machine.browser} is configured to save, usually {machine.defaultFolderHint}.</span>
                  </div>
                </>
              ) : (
                <div className="ui-region ui-region--pad ui-stack ui-stack--tight">
                  <span><Badge tone="warn">{machine.browser} on {machine.platform} cannot hand a folder to a web page</Badge></span>
                  <p className="ui-p ui-dense ui-text-2">
                    Choosing a download folder needs the File System Access API, which only Chromium browsers (Chrome, Edge) provide,
                    over HTTPS. This download goes to {machine.defaultFolderHint}.
                  </p>
                  <p className="ui-p ui-meta">
                    To be asked each time, turn on "Always ask where to save files" in {machine.browser}'s settings, or open Storage in
                    Chrome or Edge to pick the folder here.
                  </p>
                </div>
              )}
            </fieldset>

            {/* The folder as it stands right now */}
            {usingFolder && (
              <section className="ui-region ui-region--pad ui-stack ui-stack--tight" aria-labelledby="dl-folder-now">
                <h3 className="ui-heading" id="dl-folder-now">{handle.name}, as it is now</h3>
                {inspecting ? (
                  <Skeleton width="50%" label="Reading the folder" />
                ) : !inspection ? null : inspection.permission !== 'granted' ? (
                  <p className="ui-p ui-dense ui-text-2">
                    This page cannot read the folder until you allow it. You are asked when the download starts, and the contents check runs then.
                  </p>
                ) : (
                  <>
                    <p className="ui-p ui-dense ui-text-2">
                      {inspection.files.toLocaleString()}{inspection.truncated ? '+' : ''} file{inspection.files === 1 && !inspection.truncated ? '' : 's'}
                      {inspection.folders > 0 && `, ${inspection.folders.toLocaleString()}${inspection.truncated ? '+' : ''} folder${inspection.folders === 1 && !inspection.truncated ? '' : 's'}`}
                      {inspection.bytes > 0 && `, ${formatBytes(inspection.bytes)}`}
                      {inspection.truncated && ' (first 400 items; the folder is bigger)'}
                    </p>
                    {inspection.error && <p className="ui-p ui-meta">{inspection.error}</p>}
                    {nameIsDir && (
                      <p className="ui-p ui-dense"><Badge tone="danger">Name taken</Badge> A folder here is already called "{name}". Rename the download.</p>
                    )}
                    {collision && !nameIsDir && (
                      <div className="ui-stack ui-stack--tight">
                        <span><Badge tone="warn">"{collision.name}" is already in this folder</Badge></span>
                        <span className="ui-meta">{formatBytes(collision.size)}, modified {new Date(collision.lastModified).toLocaleString()}</span>
                        <fieldset className="ui-cluster ui-fieldset">
                          <legend className="ui-sr">If the name is taken</legend>
                          <Radio name="storage-collision" checked={collisionMode === 'keep'} onChange={() => setCollisionMode('keep')} label='Keep both (saves as "name (2)")' />
                          <Radio name="storage-collision" checked={collisionMode === 'overwrite'} onChange={() => setCollisionMode('overwrite')} label="Replace it" />
                        </fieldset>
                        {collisionMode === 'overwrite' && (
                          <p className="ui-p ui-meta">
                            The existing file is overwritten. If the transfer is interrupted, the existing file is already gone and what remains is a
                            partial copy you can resume; it is not rolled back.
                          </p>
                        )}
                      </div>
                    )}
                    {!collision && <span><Badge tone="ok">Name is free: nothing here will be replaced</Badge></span>}
                  </>
                )}
              </section>
            )}

            {usingFolder && (
              <Input label="Save as" value={name} onChange={e => setName(e.target.value)} spellCheck={false} autoComplete="off" />
            )}

            {/* This machine */}
            <div className="ui-stack ui-stack--tight">
              <span>
                <button type="button" className="ui-disclosure ui-dense ui-text-2" aria-expanded={showMachine} onClick={() => setShowMachine(v => !v)}>
                  <Icon icon={ChevronDown} />This machine
                </button>
              </span>
              {tightSpace && (
                <p className="ui-p ui-dense" role="alert">
                  <Badge tone="danger">Low headroom</Badge> This file is {formatBytes(sizeBytes)} but the browser only reports {formatBytes(machine.headroomBytes)} of
                  headroom on this machine. Check the volume has room before starting.
                </p>
              )}
              {showMachine && (
                <dl className="ui-kv">
                  <dt>Operating system</dt><dd>{machine.platform}</dd>
                  <dt>Browser</dt><dd>{machine.browser}</dd>
                  <dt>Folder selection</dt>
                  <dd>{machine.canPickFolder ? 'Supported' : machine.secureContext ? 'Not supported by this browser' : 'Blocked: the page is not on HTTPS'}</dd>
                  <dt>Space headroom</dt>
                  <dd>{machine.headroomBytes != null ? `${formatBytes(machine.headroomBytes)} (browser estimate, not free disk)` : 'Not reported by this browser'}</dd>
                  {machine.downlinkMbps != null && (
                    <><dt>Connection</dt><dd>{`About ${machine.downlinkMbps} Mbps${machine.effectiveType ? `, ${machine.effectiveType}` : ''}${machine.saveData ? ', data saver on' : ''}`}</dd></>
                  )}
                  {machine.cores != null && (
                    <><dt>CPU and memory</dt><dd>{`${machine.cores} cores${machine.deviceMemoryGb != null ? `, about ${machine.deviceMemoryGb} GB RAM` : ''}`}</dd></>
                  )}
                  <dt>Network</dt><dd>{machine.online ? 'Online' : 'Offline'}</dd>
                </dl>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Dock ──────────────────────────────────────────────────────────────────────
// Transfers outlive the modal and the panel, so progress renders from the job registry. A running download arms a
// beforeunload guard: closing the tab kills the stream and it cannot be resumed from another page load.
// The dock sits left of the global Upload sheet while that shows, so the two never overlap.

export function StorageDownloadDock() {
  const [jobs, setJobs] = useState([]);
  const { items: uploads } = useUploads();
  useEffect(() => subscribeDownloads(setJobs), []);

  const anyRunning = jobs.some(j => j.status === 'running');
  useEffect(() => {
    if (!anyRunning) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyRunning]);

  if (jobs.length === 0) return null;
  return (
    <section className={`ui ui-dock${uploads.length ? ' ui-dock--beside' : ''}`} aria-label="Downloads">
      {jobs.map(job => <DockRow key={job.id} job={job} />)}
    </section>
  );
}

function DockRow({ job }) {
  const total    = job.totalBytes;
  const pct      = total ? Math.min(100, (job.receivedBytes / total) * 100) : null;
  const running  = job.status === 'running';
  const done     = job.status === 'done';
  const handed   = job.status === 'handed-off';
  const failed   = job.status === 'error';
  const canceled = job.status === 'canceled';
  const rate = fmtRate(job.rateBps);
  const eta = fmtEta(total != null ? total - job.receivedBytes : null, job.rateBps);

  return (
    <article className="ui-dock__item" aria-label={`Download of ${clean(job.fileName)}`}>
      <div className="ui-cluster">
        <span className="ui-stack ui-stack--tight ui-truncate">
          <span className="ui-truncate" title={job.fileName}>{clean(job.fileName)}</span>
          <span className="ui-meta ui-truncate">{job.destKind === 'folder' ? `To ${job.destName}` : 'To browser downloads'}</span>
        </span>
        {running
          ? <Button variant="quiet" icon={CircleStop} onClick={() => job.cancel && job.cancel()}>Stop</Button>
          : <Button variant="quiet" iconOnly icon={X} label={`Dismiss ${clean(job.fileName)}`} onClick={() => dismissDownload(job.id)} />}
      </div>

      {running && (
        <>
          <span className="ui-meter" role="progressbar" aria-label={`${clean(job.fileName)} downloaded`} aria-valuemin={0} aria-valuemax={100}
            aria-valuenow={pct != null ? Math.round(pct) : undefined}>
            <span className="ui-meter__fill" style={{ width: pct != null ? `${pct}%` : '100%' }} />
          </span>
          <span className="ui-cluster ui-meta">
            <span className="ui-tc">
              {formatBytes(job.receivedBytes)}{total ? ` of ${formatBytes(total)}` : ''}{pct != null ? `, ${pct.toFixed(0)}%` : ''}{job.resumedFrom ? ', resumed' : ''}
            </span>
            <span className="ui-spacer" />
            {[rate, eta].filter(Boolean).join(', ')}
          </span>
        </>
      )}
      {done && <span><Badge tone="ok">{job.alreadyComplete ? 'Already downloaded: the file was complete' : `Saved, ${formatBytes(job.receivedBytes)}`}</Badge></span>}
      {handed && <span><Badge tone="ok">Download complete</Badge></span>}
      {canceled && <span className="ui-meta">Stopped. Nothing was left behind.</span>}
      {failed && (
        <div className="ui-stack ui-stack--tight">
          <span className="ui-field__error">{job.error}</span>
          {job.resumable && job.resumeWith && (
            <span className="ui-cluster">
              <Button variant="secondary" icon={RotateCw} onClick={() => resumeDownload(job.id)}>Resume</Button>
              <span className="ui-meta">{formatBytes(job.receivedBytes)} already on disk</span>
            </span>
          )}
        </div>
      )}
    </article>
  );
}

